/* @jsx jsx */

import { css, jsx } from "@emotion/react"
import Layout from "@src/components/layout"
import { colors, desktop, mobile } from "@src/style"
import { flatten, transform, shuffle } from "lodash"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { IoMdClose } from "react-icons/io"
import { MdPlayCircleOutline } from "react-icons/md"
import { H4 as H3, moduleIntro, Page, PageComponent } from "@src/components"
import { ForceGraph2D } from "react-force-graph"
import * as d3 from "d3-force-3d"
import { nodePaint } from "@src/utils"

interface AdviceNode {
  tags?: string
  id: string
  shareUrl?: string
  name?: string
  duration?: number
  city?: string
  approved: boolean
}
const AudioItem: React.FC<{
  advice: AdviceNode

  background: string
  color: string
}> = ({ advice, background, color, ...props }) => {
  const [open, setOpen] = useState(false)
  return (
    <div
      css={css`
        width: 250px;
        height: ${open ? 180 : 50}px;
        transition: height 1s;
        background: ${open ? colors.black : background};
        margin-bottom: 27px;
        font-family: DM Sans;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 15px;
        letter-spacing: -0.02em;
        text-align: left;
      `}
      {...props}
    >
      {!open && (
        <div
          onClick={() => setOpen(true)}
          css={css`
            display: flex;
            align-items: center;
            cursor: pointer;
          `}
        >
          <MdPlayCircleOutline
            css={css`
              font-size: 50px;
              padding: 8px;
            `}
          ></MdPlayCircleOutline>
          <div
            css={css`
              flex: 1;
            `}
          >
            <div>{advice.name && advice.name}</div>
            <div>
              {advice.city && advice.city} -{" "}
              {advice.duration && advice.duration} sec.
            </div>
          </div>
        </div>
      )}
      {open && advice.shareUrl && (
        <iframe
          css={css`
            width: 100%;
            height: 100%;
          `}
          src={advice.shareUrl}
          frameBorder="0"
        />
      )}
    </div>
  )
}
interface Props {
  data: {
    allGoogleSpreadsheetAdviezen: {
      edges: {
        node: AdviceNode
      }[]
    }
    datoCmsAdviesPage: {
      title?: string
      bodyNode?: any
      content?: any
      explainer: string
    }
  }
}
export const AdviezenPage: (locale: "nl" | "en") => FC<Props> = locale => ({
  data,
}) => {
  const labels: string[] = flatten(
    data.allGoogleSpreadsheetAdviezen.edges.map(advice =>
      advice.node.tags ? advice.node.tags.split(",") : []
    )
  )
  const containerRef = useRef(null)
  const fgRef = useRef()

  const handleClick = useCallback(
    node => {
      setSelectedTag(node.value)
      setSelectedColor(getColor(node.random))
    },
    [fgRef]
  )

  const [tags, setTags] = useState(
    transform(
      shuffle(labels).reduce((acc, tag) => {
        const key = tag.toLowerCase()
        if (acc[key]) {
          acc[key].count++
        } else {
          acc[key] = {
            count: 2,
            random: Math.random(),
          }
        }
        return acc
      }, {}),
      (acc, val, key) => {
        // @ts-ignore
        acc.nodes.push({
          id: key,
          value: key.toString(),
          count: val.count,
          random: val.random,
          x: Math.random() * 100 - 50,
          y: Math.random() * 100 - 50,
        })
        return acc
      },
      { nodes: [], links: [] } as {
        nodes: {
          id: number
          value: string
          count: number
          random: number
          x: number
          y: number
        }[]
        links: {}[]
      }
    )
  )

  const [nodeHover, setNodeHover] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string>(tags.nodes[0].value)
  const [selectedColor, setSelectedColor] = useState<Color>(
    getColor(tags.nodes[0].random)
  )
  const { title, bodyNode, explainer } = data.datoCmsAdviesPage
  const [nodeColors, setNodeColors] = useState<Color[]>([])

  const [containerSize, setContainerSize] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    setContainerSize({
      width: containerRef.current.getBoundingClientRect().width,
      height: containerRef.current.getBoundingClientRect().height,
    })
  }, [])

  useEffect(() => {
    if (fgRef.current) {
      const fg = fgRef.current
      fg.d3Force("collide", d3.forceCollide().radius(50))
      fg.d3Force("charge", d3.forceManyBody().strength(1))
      fg.d3Force("link", null)
      fg.d3Force("center", null)
    }
  }, [fgRef.current, containerSize])

  return (
    <Layout locale={locale}>
      {moduleIntro(
        title,
        bodyNode,
        "black",
        locale === "nl" ? "advies" : "advice"
      )}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            position: relative;
            flex: 1;
            display: flex;
          `}
        >
          <div
            ref={containerRef}
            css={css`
              flex: 1;
              height: calc(100vh - 120px);
              position: relative;
              background: ${colors.black};
              cursor: ${nodeHover ? "pointer" : "normal"};
              overflow: hidden;
              position: relative;
              canvas {
                touch-action: pan-y !important;
              }
            `}
          >
            {containerSize && (
              <ForceGraph2D
                enableZoomPanInteraction={false}
                ref={fgRef}
                width={containerSize.width}
                height={containerSize.height}
                graphData={tags}
                nodeCanvasObject={(node, ctx) =>
                  nodePaint(
                    node,
                    colors[getColor(node.random)],
                    ctx,
                    selectedTag === node.value
                  )
                }
                onNodeClick={handleClick}
                nodePointerAreaPaint={({ x, y, random, value }, color, ctx) => {
                  var w = 100 + Math.max(0, (value.length - 5) * 10)
                  var h = 50 + random * 20
                  ctx.fillStyle = color
                  ctx.fillRect(x, y, w, h)
                }}
                onEngineStop={() => fgRef.current.zoomToFit(1000, 100)}
                onNodeHover={node => setNodeHover(!!node)}
                d3AlphaMin={1}
                // warmupTicks={500}
                d3AlphaDecay={0}
                d3VelocityDecay={0.9}
              />
            )}
          </div>
          {selectedTag && (
            <div
              css={css`
                background-color: ${colors[selectedColor]};
                color: ${selectedColor === "offWhite"
                  ? colors.black
                  : colors.white};
                padding: 90px 40px;
                min-width: 320px;
                position: relative;
                overflow-y: auto;
                ${mobile} {
                  left: 0;
                  position: absolute;
                  top: 0;
                  bottom: 0;
                }
              `}
            >
              <div
                css={css`
                  position: absolute;
                  top: 27px;
                  right: 37px;
                  cursor: pointer;
                  ${desktop} {
                    display: none;
                  }
                `}
                onClick={() => setSelectedTag(null)}
              >
                <H3>
                  <IoMdClose />
                </H3>
              </div>
              <div
                css={css`
                  color: ${selectedColor === "offWhite"
                    ? colors.black
                    : colors.white};
                `}
              >
                <H3
                  css={css`
                    font-family: Snv Cond D Fixed;
                    font-size: 30px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 18px;
                    letter-spacing: -0.02em;
                    text-align: left;
                    margin-bottom: 20px;
                  `}
                >
                  {selectedTag}
                </H3>
                {data.allGoogleSpreadsheetAdviezen.edges
                  .filter(
                    advice =>
                      advice.node.approved &&
                      advice.node.tags &&
                      advice.node.tags
                        .toLowerCase()
                        .split(",")
                        .includes(selectedTag)
                  )
                  .map((advice, i) => (
                    <AudioItem
                      advice={advice.node}
                      key={`${selectedTag}-${i}-${advice.node.id}`}
                      background={colors[selectedColor]}
                      color={
                        selectedColor === "offWhite"
                          ? colors.black
                          : colors.white
                      }
                    />
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {PageComponent(title, false, data.datoCmsAdviesPage.content, locale)}
    </Layout>
  )
}

function getColor(value: number) {
  return value < 0.125
    ? "red"
    : value < 0.25
    ? "green"
    : value < 0.375
    ? "gold"
    : value < 0.5
    ? "blue"
    : value < 0.625
    ? "brown"
    : value < 0.75
    ? "oldRose"
    : value < 0.875
    ? "steelBlue"
    : "offWhite"
}
